import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AmplitudeService } from '@brighthub/logging';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  ApiService,
  GenericResponseCode,
  GenericResponseResult,
  GenericResponseResultItems,
} from '@brighthub/api';
import { clientSearchAmplitudeProps } from './client-search-analytics.util';
import {
  ClientSearchResponse,
} from './client-search-api.model';
import {
  ClientSearchPrepared,
  prepareClientSearch,
} from './client-search-query.util';

const SERVICE_OK_RESULT: GenericResponseResultItems = {
  status: 200,
  code: GenericResponseCode.SERVICE_OK,
  message: 'Success',
  suggestion: '',
  suggestionCopyKey: '',
  docs: '',
};

/**
 * Looks up clients by name or GUID for BrightNav omni-search, client list deep links, and the client
 * search autocomplete via prototype `brightnav-api` search.
 */
@Injectable({
  providedIn: 'root',
})
export class ClientSearchService {
  private readonly _api = inject(ApiService);
  private readonly _amplitude = inject(AmplitudeService);

  /**
   * Resolves client matches for a raw search string via GET `/v1/prototype/client/search` on
   * `brightnav-api` (name vs GUID from {@link prepareClientSearch}). Skip queries (empty /
   * wildcard-only names) never hit the network.
   *
   * @remarks On HTTP failure, logs `client_search_remote_error` to Amplitude then rethrows.
   */
  searchClients(rawQuery: string): Observable<ClientSearchResponse & GenericResponseResult> {
    const prep = prepareClientSearch(rawQuery);
    if (prep.kind === 'skip') {
      return of({
        result: SERVICE_OK_RESULT,
        payload: { query: '', clients: [] },
      });
    }

    return this._searchPrototype(rawQuery, prep);
  }

  /**
   * GET `/v1/prototype/client/search` on `brightnav-api` with `type=guid` when applicable.
   */
  private _searchPrototype(
    rawQuery: string,
    prep: Extract<ClientSearchPrepared, { kind: 'call' }>
  ): Observable<ClientSearchResponse & GenericResponseResult> {
    const qParam = encodeURIComponent(prep.qForApi);
    const typePart = prep.searchType === 'guid' ? '&type=guid' : '';
    const path = `/v1/prototype/client/search?q=${qParam}${typePart}`;

    return this._api.get<ClientSearchResponse>('brightnav-api', path).pipe(
      catchError((err: unknown) => {
        this._logClientSearchApiFailure(rawQuery, err);
        return throwError(() => err);
      })
    );
  }

  /** Amplitude only; does not swallow errors. */
  private _logClientSearchApiFailure(rawQuery: string, err: unknown): void {
    const { status, message } = this._clientSearchApiErrorMeta(err);
    this._amplitude.logEvent('client_search_remote_error', {
      ...clientSearchAmplitudeProps(rawQuery),
      message,
      status,
    });
  }

  /**
   * Normalizes errors from {@link ApiService} (often {@link HttpErrorResponse}) and internal
   * `throwError` shapes so telemetry stays consistent.
   */
  private _clientSearchApiErrorMeta(err: unknown): {
    status: number;
    message: string;
  } {
    if (err instanceof HttpErrorResponse) {
      return { status: err.status, message: err.message };
    }
    if (err && typeof err === 'object') {
      const o = err as Record<string, unknown>;
      const status = typeof o['status'] === 'number' ? o['status'] : 0;
      const message =
        typeof o['message'] === 'string'
          ? o['message']
          : err instanceof Error
            ? err.message
            : 'unknown';
      return { status, message };
    }
    return {
      status: 0,
      message: err instanceof Error ? err.message : 'unknown',
    };
  }
}
